import React, { useState } from 'react';
import {
  Button,
  Switch,
  Popover,
  Checkbox,
  Segmented,
  Space,
  Spin,
  Tooltip,
  Divider,
  Modal,
  Input,
  message,
} from 'antd';
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  ReloadOutlined,
  NodeIndexOutlined,
  FieldTimeOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useGantt } from '../context/GanttContext';
import { ZoomLevel, ZoomPresets, ZoomLevelOrder, FULLSCREEN_Z } from '../types/gantt.types';
import { tasksApis } from '../../../api/NewTasks';
import WbsCodeToggle from '../../../pages/ProjectScheduleManagement/shared/WbsCodeToggle';

const GanttToolbar: React.FC = () => {
  const {
    timelineConfig,
    showCriticalPath,
    showBaselines,
    selectedBaselines,
    baselines,
    loading,
    isFullscreen,
    projectId,
    setZoomLevel,
    setShowCriticalPath,
    setShowBaselines,
    setSelectedBaselines,
    setIsFullscreen,
    refreshData,
  } = useGantt();

  const [baselinePopoverOpen, setBaselinePopoverOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [baselineName, setBaselineName] = useState('');
  const [creating, setCreating] = useState(false);

  const zoomLevels = ZoomLevelOrder;
  const currentZoomIndex = zoomLevels.indexOf(timelineConfig.zoomLevel);

  const handleZoomIn = () => {
    if (currentZoomIndex > 0) {
      setZoomLevel(zoomLevels[currentZoomIndex - 1]);
    }
  };

  const handleZoomOut = () => {
    if (currentZoomIndex < zoomLevels.length - 1) {
      setZoomLevel(zoomLevels[currentZoomIndex + 1]);
    }
  };

  const handleBaselineToggle = (baselineId: number, checked: boolean) => {
    if (checked) {
      setSelectedBaselines([...selectedBaselines, baselineId]);
    } else {
      setSelectedBaselines(selectedBaselines.filter((id) => id !== baselineId));
    }
  };

  const handleCreateBaseline = async () => {
    if (!baselineName.trim()) {
      message.error('Please enter a baseline name');
      return;
    }

    if (!projectId) {
      message.error('Project ID is required');
      return;
    }

    setCreating(true);
    try {
      const response = await tasksApis.createBaseLine({
        projectId,
        baselineName: baselineName.trim(),
      });

      if (response.status) {
        message.success('Baseline created successfully');
        setCreateModalOpen(false);
        setBaselineName('');
        refreshData(); // Refresh to load new baseline
      } else {
        message.error(response.message || 'Failed to create baseline');
      }
    } catch (error) {
      message.error('Failed to create baseline');
      console.error('Create baseline error:', error);
    } finally {
      setCreating(false);
    }
  };

  const baselineContent = (
    <div style={{ minWidth: 220 }} onClick={(e) => e.stopPropagation()}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
        paddingBottom: 8,
        borderBottom: '1px solid #f0f0f0'
      }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Baselines</span>
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setBaselinePopoverOpen(false);
            setCreateModalOpen(true);
          }}
        >
          Create
        </Button>
      </div>

      {baselines.length === 0 ? (
        <div style={{ color: '#8c8c8c', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>
          No baselines available
        </div>
      ) : (
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {baselines.map((baseline) => (
            <div
              key={baseline.id}
              style={{
                padding: '8px 4px',
                borderBottom: '1px solid #f5f5f5',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleBaselineToggle(baseline.id, !selectedBaselines.includes(baseline.id));
              }}
            >
              <Checkbox
                checked={selectedBaselines.includes(baseline.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleBaselineToggle(baseline.id, e.target.checked);
                }}
              >
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  color: '#262626',
                }}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      background: baseline.color,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 13 }}>{baseline.name}</span>
                </span>
              </Checkbox>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          background: '#fafafa',
          borderBottom: '1px solid #d9d9d9',
        }}
      >
        {/* Left side - Zoom controls */}
        <Space size="small">
          <Button.Group>
            <Tooltip title="Zoom In">
              <Button
                icon={<ZoomInOutlined />}
                onClick={handleZoomIn}
                disabled={currentZoomIndex === 0}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Zoom Out">
              <Button
                icon={<ZoomOutOutlined />}
                onClick={handleZoomOut}
                disabled={currentZoomIndex === zoomLevels.length - 1}
                size="small"
              />
            </Tooltip>
          </Button.Group>

          <Segmented
            size="small"
            value={timelineConfig.zoomLevel}
            onChange={(value) => setZoomLevel(value as ZoomLevel)}
            options={zoomLevels.map((level) => ({
              value: level,
              label: ZoomPresets[level].label,
            }))}
          />

          <Divider type="vertical" style={{ height: 24 }} />

          <Tooltip title="Refresh">
            <Button
              icon={loading ? <Spin size="small" /> : <ReloadOutlined />}
              onClick={refreshData}
              disabled={loading}
              size="small"
            />
          </Tooltip>
        </Space>

        {/* Right side - Feature toggles */}
        <Space size="middle">
          {/* Critical Path Toggle */}
          <Space size="small">
            <NodeIndexOutlined style={{ color: showCriticalPath ? '#ff4d4f' : '#8c8c8c' }} />
            <span style={{ fontSize: 13 }}>Critical Path</span>
            <Switch
              size="small"
              checked={showCriticalPath}
              onChange={setShowCriticalPath}
            />
          </Space>

          <Divider type="vertical" style={{ height: 24 }} />

          {/* Baselines Toggle & Selection */}
          <Space size="small">
            <FieldTimeOutlined style={{ color: showBaselines ? '#1890ff' : '#8c8c8c' }} />
            <span style={{ fontSize: 13 }}>Baselines</span>
            <Switch
              size="small"
              checked={showBaselines}
              onChange={(checked) => {
                setShowBaselines(checked);
                if (!checked) {
                  setSelectedBaselines([]);
                }
              }}
            />
            {showBaselines && (
              <Popover
                content={baselineContent}
                trigger="click"
                placement="bottomRight"
                open={baselinePopoverOpen}
                onOpenChange={(open) => setBaselinePopoverOpen(open)}
                overlayStyle={{ zIndex: isFullscreen ? FULLSCREEN_Z.popupBase + 30 : 1050 }}
              >
                <Button size="small" type="default" style={{ padding: '0 4px' }}>
                  {selectedBaselines.length > 0
                    ? `(${selectedBaselines.length} selected)`
                    : 'Select'}
                </Button>
              </Popover>
            )}
          </Space>

          <Divider type="vertical" style={{ height: 24 }} />

          {/* WBS code visibility — shared with every other PMS screen */}
          <WbsCodeToggle size="small" />

          {/* Fullscreen Toggle */}
          <Tooltip title={isFullscreen ? 'Exit Fullscreen (ESC)' : 'Fullscreen'}>
            <Button
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setIsFullscreen(!isFullscreen)}
              size="small"
              type={isFullscreen ? 'primary' : 'default'}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Create Baseline Modal */}
      <Modal
        title="Create New Baseline"
        open={createModalOpen}
        onOk={handleCreateBaseline}
        onCancel={() => {
          setCreateModalOpen(false);
          setBaselineName('');
        }}
        confirmLoading={creating}
        okText="Create"
        zIndex={isFullscreen ? FULLSCREEN_Z.modal : 1000}
        maskStyle={{ zIndex: isFullscreen ? FULLSCREEN_Z.modalMask : 950 }}
      >
        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Baseline Name
          </label>
          <Input
            placeholder="Enter baseline name"
            value={baselineName}
            onChange={(e) => setBaselineName(e.target.value)}
            onPressEnter={handleCreateBaseline}
            autoFocus
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
            A baseline captures the current schedule for future comparison.
          </div>
        </div>
      </Modal>
    </>
  );
};

export default GanttToolbar;
